import { z } from 'zod';
import { basisSchema, calcMethodSchema, confidenceSchema, inferenceSourceSchema } from './charge';

// --- Line Item ---
export const lineItemInputSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().nullable().optional(),
  quantity: z.number().int().min(0).nullable().optional().transform((v) => (v === 0 ? null : v)),
  unitCost: z.number().int(),
  section: z.string().nullable().optional(),
  sortOrder: z.number().int().default(0),
  notes: z.string().nullable().optional(),
});

export const updateLineItemSchema = lineItemInputSchema.partial();

// --- Additional Charge ---
// Refinement shared by create + update: when calcMethod='percentage', rateBps and basis are required.
// When calcMethod='flat' (or omitted, which defaults to flat on create), they must be null/absent.
function refineChargeFormula(
  data: {
    calcMethod?: 'flat' | 'percentage';
    rateBps?: number | null;
    basis?: unknown;
  },
  ctx: z.RefinementCtx,
) {
  // Absent calcMethod is treated as 'flat' for the purposes of validation.
  const method = data.calcMethod ?? 'flat';
  if (method === 'percentage') {
    if (data.rateBps == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['rateBps'],
        message: 'rateBps required when calcMethod is percentage',
      });
    }
    if (data.basis == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['basis'],
        message: 'basis required when calcMethod is percentage',
      });
    }
  } else {
    if (data.rateBps != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['rateBps'],
        message: 'rateBps must be null when calcMethod is flat',
      });
    }
    if (data.basis != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['basis'],
        message: 'basis must be null when calcMethod is flat',
      });
    }
  }
}

// Base object form (no refinement) — exposed so callers can `.extend()` it.
// Use the refined `chargeInputSchema` for actual validation.
export const chargeInputObject = z.object({
  type: z.enum(['delivery', 'labor', 'tax', 'other']),
  description: z.string().nullable().optional(),
  amount: z.number().int(),
  sortOrder: z.number().int().default(0),
  // calcMethod is optional; the server treats absent as 'flat' when persisting.
  // We deliberately do not `.default('flat')` here so the inferred output type
  // stays optional and existing call sites that omit calcMethod still typecheck.
  calcMethod: calcMethodSchema.optional(),
  rateBps: z.number().int().nullable().optional(),
  basis: basisSchema.nullable().optional(),
  // Provenance hints — optional on input. Server may override these (e.g. flip
  // to 'user' when the formula changes). When the form sends them, the server
  // can persist them as-is.
  inferenceSource: inferenceSourceSchema.optional(),
  confidence: confidenceSchema.nullable().optional(),
});

export const chargeInputSchema = chargeInputObject.superRefine(refineChargeFormula);

// Update schema is a partial that still applies the formula refine. `calcMethod`
// may be omitted on update (we leave whatever's currently stored alone).
export const updateChargeObject = z.object({
  type: z.enum(['delivery', 'labor', 'tax', 'other']).optional(),
  description: z.string().nullable().optional(),
  amount: z.number().int().optional(),
  sortOrder: z.number().int().optional(),
  calcMethod: calcMethodSchema.optional(),
  rateBps: z.number().int().nullable().optional(),
  basis: basisSchema.nullable().optional(),
});

export const updateChargeSchema = updateChargeObject.superRefine(refineChargeFormula);

// --- Add-On ---
export const addOnInputSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().nullable().optional(),
  quantity: z.number().int().min(0).nullable().optional().transform((v) => (v === 0 ? null : v)),
  unitCost: z.number().int(),
  section: z.string().nullable().optional(),
  sortOrder: z.number().int().default(0),
  notes: z.string().nullable().optional(),
});

export const updateAddOnSchema = addOnInputSchema.partial();

// --- Package ---
export const packageInputSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().nullable().optional(),
  isSelected: z.boolean().default(false),
  sortOrder: z.number().int().default(0),
  lineItems: z.array(lineItemInputSchema).default([]),
  additionalCharges: z.array(chargeInputSchema).default([]),
});

export const updatePackageSchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  isSelected: z.boolean().optional(),
  sortOrder: z.number().int().optional(),
});

// --- Reorder ---
export const reorderSchema = z.array(z.object({
  id: z.string().uuid(),
  sortOrder: z.number().int(),
}));

// --- Add-On Selection ---
export const addOnSelectionSchema = z.object({
  packageId: z.string().uuid(),
  addOnId: z.string().uuid(),
});

// --- Full Quote Create (nested) ---
export const createFullQuoteSchema = z.object({
  itemId: z.string().uuid(),
  name: z.string().min(1, 'Name is required'),
  notes: z.string().nullable().optional(),
  sourceDocumentUrl: z.string().nullable().optional(),
  sourceDocumentMimeType: z.string().nullable().optional(),
  extractionSnapshot: z.unknown().optional(),
  packages: z.array(packageInputSchema).min(1, 'At least one package is required'),
  addOns: z.array(addOnInputSchema).default([]),
  additionalCharges: z.array(chargeInputSchema).default([]),
});

// --- Full Quote Replace (for editor save of existing quote) ---
export const replaceFullQuoteSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  notes: z.string().nullable().optional(),
  sourceDocumentUrl: z.string().nullable().optional(),
  sourceDocumentMimeType: z.string().nullable().optional(),
  extractionSnapshot: z.unknown().optional(),
  packages: z.array(packageInputSchema).min(1, 'At least one package is required'),
  addOns: z.array(addOnInputSchema).default([]),
  additionalCharges: z.array(chargeInputSchema).default([]),
});

export type LineItemInput = z.infer<typeof lineItemInputSchema>;
export type ChargeInput = z.infer<typeof chargeInputSchema>;
export type AddOnInput = z.infer<typeof addOnInputSchema>;
export type PackageInput = z.infer<typeof packageInputSchema>;
export type CreateFullQuoteInput = z.infer<typeof createFullQuoteSchema>;
export type ReplaceFullQuoteInput = z.infer<typeof replaceFullQuoteSchema>;
export type ReorderInput = z.infer<typeof reorderSchema>;
